// Admin → Integrações e Admin → IA (somente admin). Segredos e chaves chegam EM CLARO:
// decisão do dono (12/10/2026) — a proteção é o acesso de admin.

export interface IntegrationField {
  key: string;
  label: string;
  help?: string;
  secret: boolean;
  required: boolean;
  default?: string;
  env_var?: string;
  value: string;
  /** table | env | default | empty — de onde vem o valor em uso. */
  source: 'table' | 'env' | 'default' | 'empty';
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface Integration {
  key: string;
  name: string;
  group: string;
  description: string;
  docs_url?: string;
  testable: boolean;
  fields: IntegrationField[];
  configured: boolean;
  missing: string[];
}

export interface IntegrationHistory {
  id: number;
  integration: string;
  key: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  changed_by: string;
}

export interface TestResult {
  ok: boolean;
  message: string;
}

export type AIPurpose = 'vision' | 'embedding' | 'copywriting' | 'landing' | 'email';
export type AIKind = 'anthropic' | 'openai_compatible';

export const AI_PURPOSE_LABELS: Record<AIPurpose, string> = {
  vision: 'Visão (catalogação de mídias)',
  embedding: 'Embeddings (busca semântica)',
  copywriting: 'Texto (legendas, hashtags, ganchos)',
  landing: 'Landing pages (HTML)',
  email: 'E-mail marketing (HTML)',
};

export const AI_KIND_LABELS: Record<AIKind, string> = {
  anthropic: 'Anthropic (Claude)',
  openai_compatible: 'OpenAI ou compatível (OpenRouter, Azure, Kimi…)',
};

export interface AIProvider {
  id: number;
  name: string;
  purpose: AIPurpose;
  kind: AIKind;
  base_url: string | null;
  model: string;
  api_key: string | null;
  params_json: string | null;
  input_usd_per_mtok: number | null;
  output_usd_per_mtok: number | null;
  cache_read_usd_per_mtok: number | null;
  cache_write_usd_per_mtok: number | null;
  is_active: boolean;
  activated_at: string | null;
  activated_by: string | null;
  activation_note: string | null;
  source_crm_provider_id: number | null;
  created_at: string;
  created_by: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AIProviderForm {
  name: string;
  purpose: AIPurpose;
  kind: AIKind;
  base_url: string | null;
  model: string;
  api_key: string | null;
  params_json: string | null;
  input_usd_per_mtok: number | null;
  output_usd_per_mtok: number | null;
  cache_read_usd_per_mtok: number | null;
  cache_write_usd_per_mtok: number | null;
}

export interface AIModelInfo {
  id: string;
  display_name?: string;
}

export interface AIAgent {
  id: number;
  agent_key: string;
  name: string;
  purpose: AIPurpose;
  description: string | null;
  system_prompt: string | null;
  default_prompt: string;
  effort: string | null;
  max_tokens: number | null;
  temperature: number | null;
  variations: number | null;
  max_frames: number | null;
  is_enabled: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AIAgentForm {
  system_prompt?: string | null;
  effort?: string | null;
  max_tokens?: number | null;
  temperature?: number | null;
  variations?: number | null;
  max_frames?: number | null;
  is_enabled?: boolean;
}

export interface AIBrandVoice {
  id: number;
  name: string;
  tone: string | null;
  guidelines: string | null;
  forbidden_words: string | null;
  emoji_policy: string | null;
  examples: string | null;
  is_default: boolean;
  is_active: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export type BudgetState = 'ok' | 'alert' | 'blocked' | 'exceeded' | 'unlimited';

export interface AIBudget {
  id: number;
  scope: 'total' | AIPurpose;
  monthly_limit_usd: number | null;
  alert_percent: number;
  hard_block: boolean;
  updated_at: string | null;
  updated_by: string | null;
  spent_usd: number;
  percent: number;
  state: BudgetState;
}

export interface AIUsageGroup {
  key: string;
  label: string;
  calls: number;
  errors: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  images: number;
  cost_usd: number;
  avg_duration_ms: number;
}

export interface AIUsageCall {
  id: number;
  created_at: string;
  purpose: AIPurpose;
  feature: string;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  duration_ms: number;
  success: boolean;
  error_message: string | null;
  user_email: string | null;
  media_id: number | null;
  social_post_id: number | null;
}

export interface ProviderUsageGroup {
  vendor: string;
  model: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number | null;
}

export interface AIUsageDashboard {
  from: string;
  to: string;
  totals: AIUsageGroup;
  by_feature: AIUsageGroup[];
  by_model: AIUsageGroup[];
  by_user: AIUsageGroup[];
  by_day: AIUsageGroup[];
  top_calls: AIUsageCall[];
  recent_errors: AIUsageCall[];
  budgets: AIBudget[];
  provider_usage: ProviderUsageGroup[];
  provider_synced_at: string | null;
  avg_cost_per_image: number | null;
  avg_cost_per_video: number | null;
  avg_cost_per_caption: number | null;
}

export const AI_FEATURE_LABELS: Record<string, string> = {
  media_describe: 'Catalogação de mídias',
  media_embed: 'Vetor das mídias',
  search_embed: 'Busca semântica',
  caption: 'Legendas',
  hashtags: 'Hashtags',
  hooks: 'Ganchos e roteiros',
  rewrite: 'Reescrita',
  provider_test: 'Teste de provedor',
  landing_html: 'Assistente das landing pages',
  email_html: 'Assistente do e-mail marketing',
};

export const BUDGET_SCOPE_LABELS: Record<string, string> = {
  total: 'Total do Marketing',
  ...AI_PURPOSE_LABELS,
};
